import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Eye, EyeOff, MapPin, CheckCircle, Waves, Sprout, TrendingUp, Zap } from 'lucide-react'

const INPUT_BASE = {
  width: '100%',
  padding: '0.75rem 1rem',
  background: 'rgba(255,255,255,0.06)',
  border: '1.5px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  color: 'white',
  fontFamily: 'DM Sans, sans-serif',
  fontSize: '0.9375rem',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  boxSizing: 'border-box',
}

function FocusInput({ style = {}, ...props }) {
  const [focused, setFocused] = useState(false)
  return (
    <input
      {...props}
      style={{
        ...INPUT_BASE,
        ...style,
        borderColor: focused ? 'var(--teal)' : 'rgba(255,255,255,0.12)',
        boxShadow: focused ? '0 0 0 3px rgba(14,170,164,0.18)' : 'none',
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  )
}

const SKILL_LEVELS = [
  { value: 'beginner', label: 'Beginner', desc: 'Never surfed before', Icon: Sprout },
  { value: 'intermediate', label: 'Intermediate', desc: 'Can ride green waves', Icon: TrendingUp },
  { value: 'advanced', label: 'Advanced', desc: 'Comfortable in lineup', Icon: Zap },
]

const LABEL_STYLE = {
  display: 'block', marginBottom: 6,
  color: 'rgba(255,255,255,0.4)', fontSize: '0.8125rem',
  fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
  letterSpacing: '0.04em', textTransform: 'uppercase',
}

export default function SchoolSignupPage() {
  const { schoolSlug } = useParams()
  const navigate = useNavigate()
  const [school, setSchool] = useState(null)
  const [loadingSchool, setLoadingSchool] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', age: '', password: '', skill_level: 'beginner'
  })
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const loadSchool = async () => {
      try {
        const res = await fetch(`/api/school?action=by-slug&slug=${encodeURIComponent(schoolSlug)}`)
        if (!res.ok) { setNotFound(true); setLoadingSchool(false); return }
        const { school: matched } = await res.json()
        setSchool(matched)
      } catch {
        setNotFound(true)
      }
      setLoadingSchool(false)
    }
    loadSchool()
  }, [schoolSlug])

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (!form.full_name || !form.email || !form.password || !form.age) {
      setError('Please fill in all required fields.')
      return
    }
    if (parseInt(form.age) < 5 || parseInt(form.age) > 100) {
      setError('Please enter a valid age.')
      return
    }
    setLoading(true)

    const { data: authData, error: signUpError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: {
          full_name: form.full_name,
          role: 'customer',
          phone: form.phone,
          age: parseInt(form.age),
          school_id: school.id,
          skill_level: form.skill_level,
        }
      }
    })

    if (signUpError) {
      setError(signUpError.message)
      setLoading(false)
      return
    }

    if (authData?.user) {
      await fetch('/api/auth/register-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Pass the signup token so the server can verify the userId server-side
          ...(authData.session?.access_token
            ? { Authorization: `Bearer ${authData.session.access_token}` }
            : {}),
        },
        body: JSON.stringify({
          userId: authData.user.id,
          full_name: form.full_name,
          phone: form.phone,
          age: parseInt(form.age),
          school_id: school.id,
          skill_level: form.skill_level,
        }),
      })
    }

    setLoading(false)
    setSuccess(true)
  }

  // ── Loading ──
  if (loadingSchool) return (
    <div style={{ minHeight: '100vh', background: '#0B1E3D', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        width: 40, height: 40,
        border: '3px solid rgba(232,97,74,0.2)',
        borderTopColor: 'var(--coral)',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )

  // ── Not found ──
  if (notFound) return (
    <div style={{
      minHeight: '100vh', background: '#0B1E3D',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '2rem', textAlign: 'center',
    }}>
      <div>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'rgba(232,97,74,0.12)',
          border: '1px solid rgba(232,97,74,0.2)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '1.5rem',
        }}>
          <Waves size={28} color="var(--coral)" strokeWidth={1.5} />
        </div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', color: 'white', marginBottom: '0.75rem' }}>
          School not found
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.4)', marginBottom: '2rem', fontFamily: 'DM Sans, sans-serif' }}>
          This signup link doesn't match any active surf school.
        </p>
        <button onClick={() => navigate('/')} style={{
          background: 'linear-gradient(135deg, var(--coral) 0%, #c94d38 100%)',
          border: 'none', color: 'white', padding: '10px 28px',
          borderRadius: 10, cursor: 'pointer',
          fontSize: '0.875rem', fontWeight: 700,
          fontFamily: 'DM Sans, sans-serif',
          boxShadow: '0 4px 16px rgba(232,97,74,0.3)',
        }}>
          Go Home
        </button>
      </div>
    </div>
  )

  // ── Success ──
  if (success) return (
    <div style={{
      minHeight: '100vh', background: '#0B1E3D',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '2rem', textAlign: 'center', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
        width: 500, height: 300,
        background: 'radial-gradient(ellipse, rgba(14,170,164,0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div style={{ maxWidth: 420, position: 'relative' }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: 'rgba(14,170,164,0.12)',
          border: '1.5px solid rgba(14,170,164,0.3)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '1.5rem',
          boxShadow: '0 0 40px rgba(14,170,164,0.15)',
        }}>
          <CheckCircle size={32} color="var(--teal)" strokeWidth={1.75} />
        </div>
        <h1 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '2.25rem', color: 'white', marginBottom: '0.75rem',
        }}>
          You're in!
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: '0.5rem', fontFamily: 'DM Sans, sans-serif' }}>
          Welcome to <strong style={{ color: 'white' }}>{school.name}</strong>.
        </p>
        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.875rem', marginBottom: '2.5rem', fontFamily: 'DM Sans, sans-serif' }}>
          Check your email to confirm your account, then sign in to book your first lesson.
        </p>
        <button onClick={() => navigate('/login')} style={{
          background: 'linear-gradient(135deg, var(--coral) 0%, #c94d38 100%)',
          border: 'none', color: 'white', padding: '14px 40px',
          borderRadius: 12, cursor: 'pointer',
          fontSize: '1rem', fontWeight: 700,
          fontFamily: 'DM Sans, sans-serif',
          boxShadow: '0 4px 24px rgba(232,97,74,0.35)',
        }}>
          Sign In →
        </button>
      </div>
    </div>
  )

  // ── Main form ──
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: '#0B1E3D',
    }}>
      {/* Left panel */}
      <div style={{ flex: '0 0 42%', minHeight: '100vh' }} className="signup-left-panel">
        <div style={{
          width: '100%', height: '100%', minHeight: '100vh',
          background: 'linear-gradient(160deg, #0B1E3D 0%, #0d2847 50%, #0a1f3a 100%)',
          position: 'relative', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '3rem 2.5rem',
        }}>
          {/* Ambient orbs */}
          <div style={{
            position: 'absolute', top: '10%', left: '15%',
            width: 300, height: 300,
            background: 'radial-gradient(circle, rgba(14,170,164,0.16) 0%, transparent 70%)',
            borderRadius: '50%', filter: 'blur(40px)',
          }} />
          <div style={{
            position: 'absolute', bottom: '15%', right: '5%',
            width: 240, height: 240,
            background: 'radial-gradient(circle, rgba(232,97,74,0.12) 0%, transparent 70%)',
            borderRadius: '50%', filter: 'blur(40px)',
          }} />

          {/* Wave SVGs */}
          <svg style={{ position: 'absolute', bottom: 0, left: 0, right: 0, opacity: 0.12 }}
            viewBox="0 0 800 200" preserveAspectRatio="none">
            <path d="M0,100 C133,40 267,160 400,100 C533,40 667,160 800,100 L800,200 L0,200Z" fill="var(--teal)" />
          </svg>
          <svg style={{ position: 'absolute', bottom: 0, left: 0, right: 0, opacity: 0.07 }}
            viewBox="0 0 800 200" preserveAspectRatio="none">
            <path d="M0,120 C100,60 200,180 400,120 C600,60 700,180 800,120 L800,200 L0,200Z" fill="var(--coral)" />
          </svg>

          {/* Content */}
          <div style={{ position: 'relative', textAlign: 'center' }}>
            {/* School logo mark */}
            <div style={{
              width: 72, height: 72,
              background: 'linear-gradient(135deg, var(--coral) 0%, #c94d38 100%)',
              borderRadius: '50% 50% 50% 0',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              transform: 'rotate(-45deg)', marginBottom: '1.75rem',
              boxShadow: '0 8px 32px rgba(232,97,74,0.35)',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{ transform: 'rotate(45deg)' }}>
                <path d="M2 12 C5 8 8 16 12 12 C16 8 19 16 22 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
                <path d="M2 16 C5 12 8 20 12 16 C16 12 19 20 22 16" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
              </svg>
            </div>

            <h1 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: '2rem', fontWeight: 900,
              color: 'white', lineHeight: 1.1, marginBottom: '0.5rem',
            }}>
              {school.name}
            </h1>

            {school.location && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                color: 'rgba(255,255,255,0.35)', marginBottom: '1.25rem',
                fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem',
              }}>
                <MapPin size={13} strokeWidth={1.75} />
                {school.location}
              </div>
            )}

            {school.description && (
              <p style={{
                color: 'rgba(255,255,255,0.35)',
                fontSize: '0.875rem', lineHeight: 1.65,
                fontFamily: 'DM Sans, sans-serif',
                maxWidth: 280, margin: '0 auto 2.5rem',
              }}>
                {school.description}
              </p>
            )}

            {/* Trust badges */}
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 10,
              alignItems: 'center',
            }}>
              {[
                'Book lessons online anytime',
                'Track your progress',
                'Connect with instructors',
              ].map(t => (
                <div key={t} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  color: 'rgba(255,255,255,0.35)',
                  fontSize: '0.8125rem', fontFamily: 'DM Sans, sans-serif',
                }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%',
                    background: 'rgba(14,170,164,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <svg width="9" height="9" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L4 7L9 1" stroke="var(--teal)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2.5rem 2rem',
        background: 'rgba(255,255,255,0.015)',
        borderLeft: '1px solid rgba(255,255,255,0.06)',
        minHeight: '100vh',
        overflowY: 'auto',
      }}>
        <div style={{ width: '100%', maxWidth: 440 }}>

          {/* Mobile header */}
          <div className="signup-mobile-logo" style={{ display: 'none', textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{
              width: 48, height: 48,
              background: 'linear-gradient(135deg, var(--coral) 0%, #c94d38 100%)',
              borderRadius: '50% 50% 50% 0', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center',
              transform: 'rotate(-45deg)', marginBottom: '0.75rem',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ transform: 'rotate(45deg)' }}>
                <path d="M2 12 C5 8 8 16 12 12 C16 8 19 16 22 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
              </svg>
            </div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 900, color: 'white' }}>{school.name}</h2>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: '1.875rem', fontWeight: 700,
              color: 'white', marginBottom: '0.375rem',
            }}>
              Create your account
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.875rem', fontFamily: 'DM Sans, sans-serif' }}>
              Join {school.name} to book your first lesson
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.125rem' }}>

            {/* Full name */}
            <div>
              <label style={LABEL_STYLE}>Full Name *</label>
              <FocusInput
                placeholder="Your full name"
                value={form.full_name}
                onChange={set('full_name')}
                required
                autoComplete="name"
              />
            </div>

            {/* Email */}
            <div>
              <label style={LABEL_STYLE}>Email *</label>
              <FocusInput
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={set('email')}
                required
                autoComplete="email"
              />
            </div>

            {/* Phone + Age */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={LABEL_STYLE}>Phone</label>
                <FocusInput
                  type="tel"
                  placeholder="(555) 000-0000"
                  value={form.phone}
                  onChange={set('phone')}
                  autoComplete="tel"
                />
              </div>
              <div>
                <label style={LABEL_STYLE}>Age *</label>
                <FocusInput
                  type="number"
                  min={5} max={100}
                  placeholder="Age"
                  value={form.age}
                  onChange={set('age')}
                  required
                />
              </div>
            </div>

            {/* Skill level */}
            <div>
              <label style={LABEL_STYLE}>Surf Level *</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {SKILL_LEVELS.map(s => {
                  const active = form.skill_level === s.value
                  return (
                    <button key={s.value} type="button"
                      onClick={() => setForm(f => ({ ...f, skill_level: s.value }))}
                      style={{
                        padding: '0.875rem 0.5rem', borderRadius: 12,
                        border: '1.5px solid',
                        borderColor: active ? 'var(--teal)' : 'rgba(255,255,255,0.1)',
                        background: active
                          ? 'linear-gradient(135deg, rgba(14,170,164,0.15) 0%, rgba(14,170,164,0.08) 100%)'
                          : 'rgba(255,255,255,0.03)',
                        color: active ? 'var(--teal)' : 'rgba(255,255,255,0.4)',
                        cursor: 'pointer', textAlign: 'center',
                        transition: 'all 0.2s',
                        fontFamily: 'DM Sans, sans-serif',
                        boxShadow: active ? '0 0 0 1px rgba(14,170,164,0.2), 0 4px 16px rgba(14,170,164,0.1)' : 'none',
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                        <s.Icon size={18} strokeWidth={1.75} />
                      </div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700 }}>{s.label}</div>
                      <div style={{ fontSize: '0.625rem', opacity: 0.65, marginTop: 2, lineHeight: 1.4 }}>{s.desc}</div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Password */}
            <div>
              <label style={LABEL_STYLE}>Password *</label>
              <div style={{ position: 'relative' }}>
                <FocusInput
                  type={showPw ? 'text' : 'password'}
                  placeholder="Min 6 characters"
                  value={form.password}
                  onChange={set('password')}
                  minLength={6}
                  required
                  style={{ paddingRight: '2.75rem' }}
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{
                  position: 'absolute', right: 12, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none',
                  cursor: 'pointer', color: 'rgba(255,255,255,0.3)',
                  padding: 0, display: 'flex', alignItems: 'center',
                }}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                padding: '0.75rem 1rem', borderRadius: 10,
                background: 'rgba(232,97,74,0.1)',
                border: '1px solid rgba(232,97,74,0.25)',
                fontSize: '0.8125rem', lineHeight: 1.5,
                color: '#f0876e', fontFamily: 'DM Sans, sans-serif',
              }}>
                {error}
              </div>
            )}

            {/* Submit */}
            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '0.875rem',
              background: loading
                ? 'rgba(232,97,74,0.5)'
                : 'linear-gradient(135deg, var(--coral) 0%, #c94d38 100%)',
              border: 'none', borderRadius: 12,
              color: 'white', fontFamily: 'DM Sans, sans-serif',
              fontSize: '0.9375rem', fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: loading ? 'none' : '0 4px 20px rgba(232,97,74,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginTop: 2,
            }}>
              {loading ? (
                <>
                  <div style={{
                    width: 16, height: 16,
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 0.7s linear infinite',
                  }} />
                  Creating account…
                </>
              ) : `Join ${school.name} →`}
            </button>

            <p style={{
              textAlign: 'center', marginTop: 4,
              fontSize: '0.8125rem', color: 'rgba(255,255,255,0.25)',
              fontFamily: 'DM Sans, sans-serif',
            }}>
              Already have an account?{' '}
              <button type="button" onClick={() => navigate('/login')} style={{
                background: 'none', border: 'none',
                color: 'var(--teal)', cursor: 'pointer',
                fontSize: '0.8125rem', fontFamily: 'DM Sans, sans-serif',
                fontWeight: 600,
              }}>
                Sign in
              </button>
            </p>
          </form>

          <p style={{
            textAlign: 'center', marginTop: '1.5rem',
            fontSize: '0.6875rem', color: 'rgba(255,255,255,0.15)',
            fontFamily: 'DM Sans, sans-serif',
          }}>
            This signup is exclusive to {school.name}
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          .signup-left-panel { display: none !important; }
          .signup-mobile-logo { display: block !important; }
        }
      `}</style>
    </div>
  )
}
